import { Component, Input, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { TranslationState } from 'src/app/shared/state/TranslationState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { Pagination } from '../../model/pagination.model';
import { AssetManagementService } from '../../../asset-management/services/asset-management.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerInfoCam, PaginatedCustomerInfoResponse } from 'src/app/features/customer/models/response.model';

@Component({
  selector: 'app-assign-cam-spec',
  templateUrl: './assign-cam-spec.component.html',
  styleUrls: ['./assign-cam-spec.component.scss'],
  standalone: false
})
export class AssignCamSpecComponent implements OnInit {
  @Input() specDesc: string;
  @Input() specId: string;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('infoModal', { static: true }) infoModal: ModalComponent;
  @ViewChild('confirmation') contentModal: TemplateRef<any>;
  @ViewChild('assignModal', { static: true }) assignModal: ModalComponent;
  @ViewChild('assignContent') assignContent: TemplateRef<any>;

  protected translationService = inject(TranslationService);
  private readonly store = inject(Store);
  private readonly assetManagementService = inject(AssetManagementService);
  private readonly alertService = inject(AlertService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  search = new FormControl<string>('');
  customerInfos: CustomerInfoCam[] = [];
  pagination: Pagination;
  selection: CustomerInfoCam[] = [];
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  assignPopupText = this.translationService.translate('assignCustomerPopupText');
  searchCustomerWarning = this.translationService.translate('searchCustomerWarningText');
  assignPopupTextFirstPart = '';
  assignPopupTextSecondPart = '';
  assignPopupTextLastPart = '';
  searchWarning = false;

  assignProcessing = false;

  tableConfig: TableConfig<CustomerInfoCam> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      //bottomAndTopPaginatorThreshold: 5,
      forceBottomAction: true,
      position: 'bottom'
    },
    onSelectionChange: (elms: CustomerInfoCam[]) => (this.selection = elms),
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {}
  };

  fetcher: PaginatorFetcher<CustomerInfoCam> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    return this.assetManagementService.searchCustomer(this.search.value, this.specId, filters.page, filters.limit).pipe(
      map((customerInfoResponse: PaginatedCustomerInfoResponse) => {
        if (customerInfoResponse != null && customerInfoResponse.error != null && customerInfoResponse.error.length > 0) {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: 'Please try again. Something went wrong.'
          } as Alert);
          return;
        } else {
          this.customerInfos = customerInfoResponse.data.customerInfos;
          this.pagination = customerInfoResponse.data.pagination;
          this.searchWarning = this.search.value.length > 0 && this.customerInfos.length === 0;
          return {
            items: this.customerInfos,
            count: this.pagination.totalRecords
          };
        }
      })
    );
  };

  constructor() {}

  ngOnInit(): void {
    this.specDesc = this.route.snapshot.paramMap.get('specDesc');
    this.specId = this.route.snapshot.paramMap.get('specId');
    this.search.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe((search: string) => {
      this.tableRef.patchExtraFilters({ search });
    });

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        code: {
          cell: (elm: CustomerInfoCam) => elm.customerCode.toString(),
          header: this.translationService.translate('customerCodeText'),
          sortable: true
        },
        name: {
          cell: (elm: CustomerInfoCam) => elm.customerName,
          header: this.translationService.translate('customerNameText'),
          sortable: true
        },
        billTo: { cell: (elm: CustomerInfoCam) => elm.billTo.toString(), header: this.translationService.translate('billToText') },
        shipTo: { cell: (elm: CustomerInfoCam) => elm.shipTo, header: this.translationService.translate('shipToText') },
        city: { cell: (elm: CustomerInfoCam) => elm.city.toString(), header: this.translationService.translate('cityText') },
        state: { cell: (elm: CustomerInfoCam) => elm.state, header: this.translationService.translate('stateText') },
        camSpec: { cell: (elm: CustomerInfoCam) => elm.camSpec, header: this.translationService.translate('camSpecCodeText') },
        status: { cell: (elm: CustomerInfoCam) => elm.active, header: this.translationService.translate('statusColumnText') }
      };
    });
  }

  onSearchClick() {
    if (this.search.value === '') {
      this.showInfoModal();
      return;
    }
    this.assetManagementService
      .searchCustomer(this.search.value, this.specId, this.currentPage, this.pageSize)
      .subscribe((customerInfoResponse: PaginatedCustomerInfoResponse) => {
        if (customerInfoResponse != null && customerInfoResponse.error != null && customerInfoResponse.error.length > 0) {
          // TODO: change the dialog and content once the error dialog design is finalized
          this.alertService.showAlert({
            type: AlertType.strong,
            content: 'Please try again. Something went wrong.'
          } as Alert);
        } else {
          this.customerInfos = customerInfoResponse.data.customerInfos;
          this.pagination = customerInfoResponse.data.pagination;
        }
      });
  }

  onCancelClick() {
    this.router.navigateByUrl('home/specifications');
  }

  onAssignClick() {
    if (this.selection.length === 0) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('assignCustomerWarningText')
      } as Alert);
    } else {
      this.assignPopupTextFirstPart = this.assignPopupText.substring(0, this.assignPopupText.search('~'));
      this.assignPopupTextSecondPart = this.assignPopupText.substring(
        this.assignPopupText.search('~') + 1,
        this.assignPopupText.search('#')
      );
      this.assignPopupTextLastPart = this.assignPopupText.substring(this.assignPopupText.search('#') + 1, this.assignPopupText.length);
      this.showAssignModal();
    }
  }

  showInfoModal(): void {
    this.infoModal.open({
      id: 'confirmation',
      width: 500,
      title: this.translationService.translate('searchButtonText'),
      content: this.contentModal,
      confirm: this.translationService.translate('okText')
    });
  }

  showAssignModal(): void {
    this.assignModal.open({
      id: 'assignContent',
      width: 750,
      title: 'Confirmation',
      content: this.assignContent,
      cancel: this.translationService.translate('cancelLinkText'),
      confirm: this.translationService.translate('confirmText')
    });
  }

  onAssignModalClosed(result: ModalResult) {
    if (result.result) {
      this.assignProcessing = true;
      const customerIds: string[] = [];
      this.selection.forEach((value) => {
        customerIds.push(value.customerId);
      });
      this.assetManagementService.assignCustomer(this.specId, customerIds).subscribe((v) => {
        if (v) {
          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('assignCustomerSuccessText')
          } as Alert);
          this.router.navigateByUrl('home/specifications');
        }
        this.assignProcessing = false;
      });
    }
  }
}
